import * as React from "react";
import {useState} from "react";
import {Dialog, DialogContent, DialogTitle, FormControl, IconButton, MenuItem, Select, styled,} from "@mui/material";
import {GetUsersOfClinicUsers, UserRoles} from "fe-common/graphql";
import {LoadingProgress} from "fe-web-components/loader/loadingProgress";
import {ReactComponent as SuccessIcon} from "../../../assets/clinic/success.svg";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {ClinicActionButton} from "fe-web-components/clinic/clinicActionButton";
import {useClinicState} from "../../../clinic/clinicStateContext";
import {useClinicContext} from "fe-web-components/clinic/ClinicContext";
import {ClinicFormInput} from "../elements/clinicFormInput";
import {clinicRoles} from "./clinicUserRoles";
import {ACTION, CATEGORY, evt} from "fe-common/analytics/analytics";
import CloseIcon from "@mui/icons-material/Close";
import {sendMessageWithCommunicationService} from "../util/sendMessageWithCommmunicationService";
import {SupportedCommunicationPlatforms} from "fe-common/commonTypes/communicationLambdaApi";
import {MessageTouchPoint} from "@kahunmedical/common-configuration/dist/configuration/messageTemplate";

//region [[ Styles ]]

const ClinicUserFormView = styled(Dialog)({});

//endregion [[ Styles ]]

const CloseButton = styled(IconButton)({
  position: "absolute",
  top: 5,
  right: 5,
});
const FormActions = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: 16,
  paddingTop: 8,
  width: 500,
});
const RoleMenuItem = styled(MenuItem)({
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
});
const RoleDescription = styled("div")({
  fontSize: "11px",
});
const FinalStageView = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  maxWidth: 370,
  gap: 10,
  padding: 30,
  "& .finalStatusIcon": {
    height: 36,
    width: 36,
  },
});
const ErrorMsg = styled("span")({
  color: "red",
});

//region [[ Props ]]

export interface ClinicUserFormProps {
  onUpdated: (userData: GetUsersOfClinicUsers) => void;
  onClose: () => void;
  userToUpdate?: GetUsersOfClinicUsers;
  users: GetUsersOfClinicUsers[];
}
enum UpdateStages {
  start,
  saveUser,
  sendInvite,
  success,
  fail,
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ClinicUserForm = ({ users, ...props }: ClinicUserFormProps) => {
  const [updatingStage, setUpdatingStage] = useState<UpdateStages>(
    UpdateStages.start,
  );
  const [errMsg, setErrMsg] = useState<string>(null);
  const [
    {
      clinicData: { id: clinicId, name: clinicName, displayName },
    },
  ] = useClinicState();
  const dao = useClinicContext();
  const isNewUser = props.userToUpdate.email === "new";
  const preventClose =
    updatingStage === UpdateStages.saveUser ||
    updatingStage === UpdateStages.sendInvite;
  const handleCloseAddDialog = () => {
    if (preventClose) return false; //don't allow closing the dialog while adding/sending
    props.onClose();
  };
  const [email, setEmail] = useState<string>(
    isNewUser ? "" : props.userToUpdate.email,
  );
  const [role, setRole] = useState(props.userToUpdate.role);

  const sendWelcomeToClinicEmail = async (
    userToAdd: string,
  ): Promise<boolean> => {
    try {
      const send = await sendMessageWithCommunicationService(
        clinicName,
        SupportedCommunicationPlatforms.Email,
        {
          to: userToAdd,
          touchPoint: MessageTouchPoint.SendNewUserInvitation,
          macroFields: {
            clinic_name: clinicName,
            clinic_display_name: displayName,
            clinic_id: clinicId,
          },
        },
      );
      return send.status === 200;
      // await sendEmailWithEmailJs({
      //   template_params: {
      //     clinic_name: displayName,
      //     to_email: userToAdd,
      //     clinic_id: clinicId,
      //   },
      //   template_id: emailKeys.EMAIL_JS_ADD_USER_TO_CLINIC_TEMPLATE_ID,
      //   user_id: emailKeys.EMAIL_JS_USER_ID,
      //   service_id: emailKeys.EMAIL_JS_NOREPLY_SERVICE_ID,
      // });
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const updateUser = async () => {
    if (isNewUser && users.find((u) => u.email === email)) {
      setErrMsg("User already exists");
      return false;
    }
    if (role === UserRoles.KahunClinicUser) {
      if (
        users.filter(
          (u) => u.role !== UserRoles.KahunClinicUser && u.email !== email,
        ).length === 0
      ) {
        setErrMsg("At least one clinic administrator is required");
        return false;
      }
    }
    let failed = false;
    try {
      setUpdatingStage(UpdateStages.saveUser);
      const status = await dao.addUserToClinic(clinicId, email, role);
      if (status === "success") {
        if (isNewUser) {
          setUpdatingStage(UpdateStages.sendInvite);
          const sent = await sendWelcomeToClinicEmail(email);
          if (sent) {
            setUpdatingStage(UpdateStages.success); //success with sending mail
          } else {
            setUpdatingStage(UpdateStages.fail); //user added but invite no sent
            setErrMsg(
              `${email} was added as a member, but an invitation could not be sent to them`,
            );
          }
        } else {
          setUpdatingStage(UpdateStages.success); //success without sending mail
        }
        // Send an event for inviting the user
        evt({
          category: CATEGORY.CLINIC_MANAGEMENT,
          action: ACTION.CLINIC_ADD_CLINIC_USER,
          props: {
            clinicName: displayName,
            invitedUser: email,
            role,
          },
        });
        props.onUpdated({ email, role });
      } else {
        failed = true;
      }
    } catch (err) {
      console.error(err);
      failed = true;
    }
    if (failed) {
      setUpdatingStage(UpdateStages.fail);
      setErrMsg(
        isNewUser
          ? `Could not add ${email} as a member`
          : `Could not update ${email}`,
      );
    }
  };

  return (
    <ClinicUserFormView open={true} onClose={handleCloseAddDialog}>
      {!preventClose && (
        <CloseButton onClick={handleCloseAddDialog}>
          <CloseIcon />
        </CloseButton>
      )}
      {updatingStage === UpdateStages.start && (
        <>
          <DialogTitle>{isNewUser ? "Add" : "Edit"} Member</DialogTitle>
          <DialogContent>
            <ClinicFormInput
              id="email"
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              disabled={!isNewUser}
            />
            <ClinicFormInput
              id="role"
              label="User Role"
              customInput={
                <FormControl variant="outlined" fullWidth>
                  <Select
                    onChange={(e) => setRole(e.target.value as UserRoles)}
                    value={role}
                    fullWidth
                  >
                    {clinicRoles.map((option, i) => (
                      <RoleMenuItem value={option.value} key={"role" + i}>
                        {option.title}
                        <RoleDescription>{option.description}</RoleDescription>
                      </RoleMenuItem>
                    ))}
                  </Select>
                </FormControl>
              }
            />
            <FormActions>
              {errMsg && <ErrorMsg>{errMsg}</ErrorMsg>}
              <ClinicActionButton
                onClick={() => updateUser()}
                disabled={!email || !role}
                label={isNewUser ? "ADD" : "SAVE"}
                style={{ marginLeft: "auto" }}
              />
            </FormActions>
          </DialogContent>
        </>
      )}
      {updatingStage === UpdateStages.saveUser && (
        <FinalStageView>
          <LoadingProgress
            label={
              isNewUser ? "Adding a new member..." : "Saving member settings..."
            }
          />
        </FinalStageView>
      )}
      {updatingStage === UpdateStages.sendInvite && (
        <FinalStageView>
          <LoadingProgress label="Sending an invitation..." />
        </FinalStageView>
      )}
      {updatingStage === UpdateStages.fail && (
        <FinalStage
          errMsg={errMsg}
          user={{ email, role }}
          isNewUser={isNewUser}
          onClose={() => handleCloseAddDialog()}
        />
      )}
      {updatingStage === UpdateStages.success && (
        <FinalStage
          errMsg={null}
          user={{ email, role }}
          isNewUser={isNewUser}
          onClose={() => handleCloseAddDialog()}
        />
      )}
    </ClinicUserFormView>
  );
};

const FinalStage = (props: {
  errMsg: string;
  user: GetUsersOfClinicUsers;
  isNewUser: boolean;
  onClose: () => void;
}) => {
  return (
    <FinalStageView>
      {!props.errMsg ? (
        <SuccessIcon className="finalStatusIcon" />
      ) : (
        <ErrorOutlineIcon color="error" className="finalStatusIcon" />
      )}
      <strong>
        {!props.errMsg ? "Thank You!" : "Sorry, something went wrong"}
      </strong>
      {!props.errMsg
        ? props.isNewUser
          ? `An email was sent to ${props.user.email} to let them know they were added to your clinic.`
          : `${props.user.email} was updated successfully`
        : props.errMsg}
      <ClinicActionButton
        onClick={props.onClose}
        label="CLOSE"
        style={{ marginTop: 24 }}
      />
    </FinalStageView>
  );
};
